import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Save, Plus, Trash2, Send, Loader2 } from 'lucide-react';

interface ServiceActivity {
  id: number | string;
  activity: string;
  details: string;
}

interface Service {
  name: string;
  startTime: string;
  endTime: string;
  orderOfService: ServiceActivity[];
}

export const ServicePlanner = ({ shopId }: { shopId: string }) => {
  // Use string for date to prevent array-reference infinite loops
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  const initialServices: Service[] = [
    { name: 'Morning Glory', startTime: '07:00', endTime: '08:00', orderOfService: [] },
    { name: 'Youth Service', startTime: '08:15', endTime: '09:30', orderOfService: [] },
    { name: 'English Service', startTime: '10:00', endTime: '12:00', orderOfService: [] },
    { name: 'Kiswahili Service', startTime: '12:00', endTime: '13:00', orderOfService: [] }
  ];

  const [services, setServices] = useState<Service[]>(initialServices);

  const fetchExistingPlan = useCallback(async () => {
    setIsLoadingDraft(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-current-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: shopId,
          date: date,
          business_category: 'church',
          fetch_mode: 'latest_if_empty'
        }),
      });

      if (response.ok) {
        const data = await response.json();
      
        if (data && Array.isArray(data.services) && data.services.length > 0) {
          setServices(data.services);
        
          if (data.record_date) {
            // Clean the date (handles string, array, or timestamp formats)
            const rawDate = Array.isArray(data.record_date) ? data.record_date[0] : data.record_date;
            const fetchedDateClean = rawDate.split('T')[0]; 

            // GUARD: Only update if different to prevent infinite loop
            if (fetchedDateClean !== date) {
              setDate(fetchedDateClean); 
            }
          }
        } else {
          setServices(initialServices);
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setServices(initialServices);
    } finally {
      setIsLoadingDraft(false);
    }
  }, [date, shopId]); // Removed initialServices from dependency to keep it stable

  useEffect(() => {
    fetchExistingPlan();
  }, [fetchExistingPlan]);

  const handleSave = async (shouldPublish: boolean = false) => {
    shouldPublish ? setIsPublishing(true) : setIsSaving(true);

    const payload = {
      shop_id: shopId,
      date,
      business_category: 'church',
      services,
      status: shouldPublish ? 'published' : 'draft',
      trigger_whatsapp: shouldPublish 
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/church-service-planner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        alert(shouldPublish ? 'Service Plan Published Successfully!' : 'Progress saved successfully.');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Failed to save service plan.');
    } finally {
      setIsPublishing(false);
      setIsSaving(false);
    }
  };

  const addActivity = (serviceIndex: number) => {
    const newServices = [...services];
    newServices[serviceIndex].orderOfService.push({
      id: Date.now(),
      activity: '',
      details: ''
    });
    setServices(newServices);
  };

  const removeActivity = (serviceIndex: number, activityId: number | string) => {
    const newServices = [...services];
    newServices[serviceIndex].orderOfService = newServices[serviceIndex].orderOfService.filter(
      item => item.id !== activityId
    );
    setServices(newServices);
  };

  const updateActivity = (serviceIndex: number, activityIndex: number, field: 'activity' | 'details', value: string) => {
    const newServices = [...services];
    newServices[serviceIndex].orderOfService[activityIndex][field] = value;
    setServices(newServices);
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Calendar className="text-blue-600" /> Sunday Service Planner
        </h2>
        <div className="flex items-center gap-3">
          {isLoadingDraft && <Loader2 className="animate-spin text-blue-500" size={18} />}
          <input 
            type="date" 
            value={date} 
            onChange={(e) => setDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>

      <div className={`space-y-6 transition-opacity ${isLoadingDraft ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
        {services.map((service, sIdx) => (
          <div key={service.name} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <span className="font-bold text-gray-800">{service.name}</span>
              <div className="flex items-center gap-2 text-sm text-gray-500 font-medium bg-white px-2 py-1 rounded border">
                <Clock size={14} className="text-blue-500" /> {service.startTime} - {service.endTime}
              </div>
            </div>
            
            <div className="space-y-4">
              {service.orderOfService.map((item, iIdx) => (
                <div key={item.id} className="relative group">
                  <div className="flex flex-col border rounded-lg overflow-hidden shadow-sm bg-white">
                    <div className="flex items-center bg-white border-b">
                       <input
                        placeholder={`Activity ${iIdx + 1} (e.g. Opening Prayer)`}
                        className="flex-1 p-2 text-sm font-semibold outline-none"
                        value={item.activity}
                        onChange={(e) => updateActivity(sIdx, iIdx, 'activity', e.target.value)}
                      />
                      <button 
                        onClick={() => removeActivity(sIdx, item.id)}
                        className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    <textarea
                      placeholder="Add details, verses, or hymn lyrics..."
                      rows={2}
                      className="w-full p-2 text-sm bg-gray-50 focus:bg-white transition-colors outline-none resize-none border-none"
                      value={item.details}
                      onChange={(e) => updateActivity(sIdx, iIdx, 'details', e.target.value)}
                    />
                  </div>
                </div>
              ))}

              <button 
                onClick={() => addActivity(sIdx)}
                className="w-full py-2 border-2 border-dashed border-gray-200 rounded-lg text-xs text-gray-500 flex items-center justify-center gap-1 hover:border-blue-300 hover:text-blue-600 transition-all bg-white/50"
              >
                <Plus size={14} /> Add Activity to {service.name}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        <button 
          onClick={() => handleSave(false)}
          disabled={isSaving || isPublishing || isLoadingDraft}
          className="bg-white border-2 border-blue-600 text-blue-600 py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-50 transition-all disabled:opacity-50"
        >
          <Save size={20} /> {isSaving ? 'Saving Draft...' : 'Save Draft'}
        </button>

        <button 
          onClick={() => handleSave(true)}
          disabled={isSaving || isPublishing || isLoadingDraft}
          className="bg-blue-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-[0.98] disabled:opacity-50"
        >
          <Send size={20} /> {isPublishing ? 'Publishing...' : 'Publish to WhatsApp'}
        </button>
      </div>
    </div>
  );
};
